/**
 * SyncedCheckPulse Component
 *
 * Subtle brand "synced" confirmation: a check-circle icon (Ignition Red) with a
 * "Synchronisiert" label (Dusknight). Plays a ZoomIn entrance plus a single
 * scale pulse. No green anywhere — the synced moment reads in brand red, not a
 * success colour. The parent owns the dwell/unmount timing; this component
 * never self-hides.
 *
 * @module components/SyncedCheckPulse
 */

import { useEffect } from "react"
import { ViewStyle, TextStyle } from "react-native"
import Animated, {
  ZoomIn,
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withTiming,
} from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import { colors, spacing } from "@/theme"

import { Text } from "./Text"

export interface SyncedCheckPulseProps {
  /**
   * Label shown next to the check icon. Defaults to "Synchronisiert".
   */
  label?: string

  /**
   * Icon size in px.
   */
  size?: number

  /**
   * Optional style override for the row container.
   */
  style?: ViewStyle
}

/**
 * SyncedCheckPulse Component
 *
 * Renders the check-circle + label pair with a one-shot celebratory pulse.
 */
export function SyncedCheckPulse({
  label = "Synchronisiert",
  size = 16,
  style: $styleOverride,
}: SyncedCheckPulseProps) {
  const scale = useSharedValue(1)

  useEffect(() => {
    // Single pulse: 1 → 1.15 (180ms) → 1 (140ms). Parent controls unmount.
    scale.value = withSequence(
      withTiming(1.15, { duration: 180 }),
      withTiming(1, { duration: 140 }),
    )
    // `scale` is a stable useSharedValue ref → effect runs once on mount.
  }, [scale])

  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  return (
    <Animated.View entering={ZoomIn.duration(200)} style={[$container, $styleOverride]}>
      <Animated.View style={animatedIconStyle}>
        <Icon name="check-circle" size={size} color={colors.primary} />
      </Animated.View>
      <Text style={$label}>{label}</Text>
    </Animated.View>
  )
}

// Styles
const $container: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xxs, // 4
}

const $label: TextStyle = {
  fontSize: 12,
  fontWeight: "600",
  color: colors.text, // Dusknight #111E2C — no green
  letterSpacing: 0, // Workaround for React Native Web text node error
}
